import { useEffect, useState, useCallback, useRef } from 'react';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { captureException } from '@sentry/react';
import { FlowStatus } from '@/api/generated';
import { useLanguage } from '@/contexts/language-context';
import { ValidationStatus } from '@/enums/validation-status';
import type { MfaFlowApiResponse } from '@/hooks/use-mfa-orchestrator.types';
import {
  startMfaFlowFromCookie,
  startEscalationAuthentication,
  validateAuthId,
  type SignOnApp,
} from '@/services/apis/mfa-orchestrator.api';
import { initiateSamlOrFallback } from '@/services/apis/saml-init.api';
import { LoginUI } from './login-ui';

interface ValidatingPageProps {
  authId: string;
  onSuccessLogin: (
    result: MfaFlowApiResponse,
    username?: string
  ) => void | Promise<void>;
  isEscalation?: boolean; // Only true for escalation flows
  isEnrollment?: boolean; // Only true for enrollment flows
}

type ValidatingState =
  | { status: ValidationStatus.VALIDATING }
  | { status: ValidationStatus.AUTHENTICATED; response: MfaFlowApiResponse }
  | { status: ValidationStatus.NEEDS_LOGIN; spApp: SignOnApp | null }
  | { status: ValidationStatus.ERROR; message: string };

export function ValidatingPage({
  authId,
  onSuccessLogin,
  isEscalation = false,
  isEnrollment = false,
}: ValidatingPageProps) {
  const { t } = useLanguage();
  // For escalation/enrollment flows, wait for respective endpoint to start the flow
  // Then navigate directly to factor (no email needed - user is known from session)
  const [state, setState] = useState<ValidatingState>({
    status: ValidationStatus.VALIDATING,
  });

  const onSuccessLoginRef = useRef(onSuccessLogin);
  // Keep the latest callback without retriggering the mount fetch.
  // eslint-disable-next-line react-hooks/refs
  onSuccessLoginRef.current = onSuccessLogin;

  // Destination SP app (SP-initiated flows only), captured at preflight so the
  // login card can render "Sign in to {app}".
  const spAppRef = useRef<SignOnApp | null>(null);

  // Guard against double API calls from React StrictMode
  // StrictMode intentionally double-invokes effects in development,
  // which would cause race conditions with login operations.
  const hasStartedRef = useRef(false);

  const checkAuthenticatedContext = useCallback(async () => {
    if (hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;

    try {
      // NOTE: Enrollment flows no longer go through web-login
      // They are handled directly by the enrollment service via user-portal-api
      // If isEnrollment is true, this is likely a misconfiguration
      if (isEnrollment) {
        console.error(
          'Enrollment flows should not use web-login iframe. Use enrollment service API directly.'
        );
        setState({
          status: ValidationStatus.ERROR,
          message: 'Enrollment is not supported in this context.',
        });

        return;
      }

      // CRITICAL: Only use escalation endpoint if this is explicitly an escalation flow
      // Regular flows should use the original cookie-based flow
      if (isEscalation && authId) {
        // For escalation flows, call escalation endpoint to start the flow
        // This will return IN_PROGRESS with password factorType
        // Then navigate directly to password factor (email already known from session)
        // NO FALLBACKS ALLOWED - escalation flows must use the escalation endpoint
        try {
          const escalationResponse =
            await startEscalationAuthentication(authId);

          if (escalationResponse.status === FlowStatus.COMPLETED) {
            setState({
              status: ValidationStatus.AUTHENTICATED,
              response: escalationResponse,
            });
            await onSuccessLoginRef.current(escalationResponse);

            return;
          } else if (escalationResponse.status === FlowStatus.IN_PROGRESS) {
            // User needs to complete escalation authentication
            // Navigate directly to password factor (email already known from session)
            await onSuccessLoginRef.current(escalationResponse);

            return;
          } else {
            // FAILED or unexpected status - show error, no fallback allowed
            console.error(
              'Escalation endpoint returned unexpected status - no fallback allowed',
              escalationResponse.status
            );
            setState({
              status: ValidationStatus.ERROR,
              message:
                'Failed to start escalation authentication. Please try again.',
            });
            captureException(
              new Error(
                'Escalation endpoint returned unexpected status - no fallback allowed'
              )
            );

            return;
          }
        } catch (error) {
          // Escalation flows MUST use the escalation endpoint - no fallbacks allowed
          // If the endpoint fails, show an error - do not fall back to login UI
          console.error(
            'Escalation endpoint failed - no fallback allowed',
            error
          );
          setState({
            status: ValidationStatus.ERROR,
            message:
              'Failed to start escalation authentication. Please try again.',
          });
          captureException(error);

          return;
        }
      }

      // PREFLIGHT: Validate pending auth session cookie before starting flow (for non-escalation flows)
      if (!isEscalation) {
        const { valid, spApp } = await validateAuthId();
        spAppRef.current = spApp;

        if (!valid) {
          // No valid pending auth session — initiate SAML directly from web-login.
          // This avoids the redirect chain: web-login → user-portal → SAML.
          // Tenant is resolved server-side from the Host header (SubdomainTenantMiddleware).
          await initiateSamlOrFallback();

          return;
        }
      }

      // ORIGINAL FLOW: Use cookie-based flow for regular authentication
      // This is the original behavior - unchanged!
      // Attempt to start flow with existing authenticated context
      const response = await startMfaFlowFromCookie(authId);

      // Check if all factors are already authenticated
      if (response.status === FlowStatus.COMPLETED) {
        setState({ status: ValidationStatus.AUTHENTICATED, response });
        await onSuccessLoginRef.current(response);
      } else if (response.status === FlowStatus.IN_PROGRESS) {
        // User needs to complete additional factors
        // Proceed directly to the factor that needs completion
        await onSuccessLoginRef.current(response);
      } else {
        // Unexpected status, fall back to login
        setState({
          status: ValidationStatus.NEEDS_LOGIN,
          spApp: spAppRef.current,
        });
      }
    } catch (error) {
      // No authenticated context or error occurred
      // Fall back to regular login flow
      console.info('No authenticated context available, showing login', error);
      setState({
        status: ValidationStatus.NEEDS_LOGIN,
        spApp: spAppRef.current,
      });
    }
  }, [authId, isEscalation, isEnrollment]);

  useEffect(() => {
    // Mount-fetch for pending-auth; setState after the request is required.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void checkAuthenticatedContext();
  }, [checkAuthenticatedContext]);

  // Re-validate the pending-auth session whenever the tab becomes visible
  // again (user switches back from another tab/app) or the network reconnects.
  // This catches the case where the 10-min pending-auth TTL elapsed while the
  // tab was hidden — we redirect to a fresh SAML flow instead of letting the
  // user submit credentials against a dead session.
  const isRecheckingRef = useRef(false);
  useEffect(() => {
    if (isEscalation || isEnrollment) {
      return;
    }

    const recheck = async () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      if (isRecheckingRef.current) {
        return;
      }

      isRecheckingRef.current = true;

      try {
        const { valid, spApp } = await validateAuthId();
        spAppRef.current = spApp;

        if (!valid) {
          await initiateSamlOrFallback();
        }
      } finally {
        isRecheckingRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('online', recheck);

    return () => {
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('online', recheck);
    };
  }, [isEscalation, isEnrollment]);

  if (
    state.status === ValidationStatus.AUTHENTICATED ||
    state.status === ValidationStatus.VALIDATING
  ) {
    return <div />;
  }

  if (state.status === ValidationStatus.NEEDS_LOGIN) {
    return (
      <LoginUI
        authId={authId}
        onSuccessLogin={onSuccessLogin}
        spApp={state.spApp}
      />
    );
  }

  return (
    <div className='w-full flex flex-1 justify-center items-center'>
      <div className='gap-5 p-4 justify-around items-center border border-secondary rounded-lg bg-primary'>
        <div className='gap-4 items-center'>
          <Text as='p' size={TextSizes.LG} className='text-error-primary'>
            {state.status === ValidationStatus.ERROR
              ? state.message
              : t('auth.error')}
          </Text>
          <button
            className='underline cursor-pointer bg-transparent border-none p-0 text-brand-secondary hover:text-brand-secondary_hover'
            onClick={() =>
              setState({
                status: ValidationStatus.NEEDS_LOGIN,
                spApp: spAppRef.current,
              })
            }
          >
            <Text size={TextSizes.MD}>{t('auth.tryAgain') || 'Try again'}</Text>
          </button>
        </div>
      </div>
    </div>
  );
}
